/**
 * デモアカウント一覧ページ
 *
 * 機能:
 * - 全業界のデモアカウント情報を一覧表示
 * - アカウント情報のコピー機能
 * - ログインページへの遷移
 */

import React from 'react';
import { Card, Typography, Tag, Button, Space, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LoginOutlined, CopyOutlined } from '@ant-design/icons';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { SEOHead } from '../components/SEOHead';

const { Title, Paragraph, Text } = Typography;

interface DemoAccount {
  industry: string;
  industryCode: string;
  viewerEmail: string;
  viewerPassword: string;
}

const demoAccounts: DemoAccount[] = [
  {
    industry: '工場',
    industryCode: 'factory',
    viewerEmail: 'demo-viewer-factory@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: 'ビルメンテナンス',
    industryCode: 'building_maintenance',
    viewerEmail: 'demo-viewer-building_maintenance@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: '学校法人',
    industryCode: 'educational_institution',
    viewerEmail: 'demo-viewer-educational_institution@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: '自治体',
    industryCode: 'municipality',
    viewerEmail: 'demo-viewer-municipality@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: 'ホテル',
    industryCode: 'hotel',
    viewerEmail: 'demo-viewer-hotel@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: '商業施設',
    industryCode: 'commercial_facility',
    viewerEmail: 'demo-viewer-commercial_facility@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
  {
    industry: '介護施設',
    industryCode: 'nursing_home',
    viewerEmail: 'demo-viewer-nursing_home@example.com',
    viewerPassword: 'demo-viewer-2024',
  },
];

export default function DemoAccountsPage() {
  const navigate = useNavigate();

  const columns: ResponsiveColumnType<DemoAccount>[] = [
    {
      title: '業界',
      dataIndex: 'industry',
      key: 'industry',
      width: 150,
      render: (text: string, record: DemoAccount) => (
        <Space>
          <Text strong>{text}</Text>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/industry-guides/${record.industryCode}`)}
          >
            詳細
          </Button>
        </Space>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: '閲覧用アカウント',
      key: 'viewer',
      render: (_: unknown, record: DemoAccount) => (
        <Space direction="vertical" size="small">
          <div>
            <Tag color="blue" icon={<CopyOutlined />}>
              Viewer
            </Tag>
          </div>
          <Text copyable={{ text: record.viewerEmail }} code style={{ fontSize: '12px' }}>
            {record.viewerEmail}
          </Text>
          <Text copyable={{ text: record.viewerPassword }} code style={{ fontSize: '12px' }}>
            {record.viewerPassword}
          </Text>
        </Space>
      ),
      mobileRole: 'description',
      mobilePriority: 2,
    },
  ];

  return (
    <>
      <SEOHead
        title="デモアカウント｜一元帳"
        description="一元帳のデモアカウントで実際の操作感を体験できます。設備管理ソフトの導入検討にご活用ください。"
      />
      <div style={{ padding: '40px 24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <Title level={2}>デモアカウント一覧</Title>
        <Paragraph style={{ fontSize: '16px' }}>
          各業界のデモアカウントで実際の画面を体験できます。
          <br />
          アカウント情報をクリックするとコピーできます。
        </Paragraph>
      </div>

      <Alert
        message="デモアカウントについて"
        description={
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>デモアカウントは<strong>閲覧専用</strong>です。データの変更・削除はできません。</li>
            <li>管理画面を含め、全ての機能を閲覧できます。</li>
            <li>複数のユーザーで共有されているため、個人情報は入力しないでください。</li>
            <li>実際の編集機能を試すには、新規登録から本登録アカウントを作成してください。</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Card>
        <ResponsiveTable
          dataSource={demoAccounts}
          columns={columns}
          rowKey="industryCode"
          pagination={false}
          bordered
          mobileCardConfig={{
            titleField: 'industry',
          }}
        />

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<LoginOutlined />}
            size="large"
            onClick={() => navigate('/login')}
          >
            ログイン画面を開く
          </Button>
        </div>
      </Card>

      <div style={{ marginTop: '32px', textAlign: 'center' }}>
        <Text type="secondary">
          各業界の詳しい使い方は{' '}
          <Button type="link" onClick={() => navigate('/industry-guides')} style={{ padding: 0 }}>
            業界別使い方ガイド
          </Button>{' '}
          をご覧ください。
        </Text>
      </div>
    </div>
    </>
  );
}
